import { BetaLabel, Loading, TrendLabel } from '@/components/customComponents'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatNumber } from '@/utils'
import React from 'react'

// TODO: when api requests are refactored, this can be completed and removed.
// Mock data generation for line chart visualization
const generateMockData = () => {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const data = []
  const N = 30
  for (let i = 0; i < N; i++) {
    const year = Math.floor(i / 12) + 2020
    const monthIndex = i % 12
    const month = months[monthIndex]

    // Main upward trend
    const trend = 20 + (i / (N - 1)) * 60
    // Sine wave for smooth undulations
    const wave = Math.sin((i / (N - 1)) * Math.PI * 2) * 6
    // Larger bump in the middle
    const bump = Math.exp(-Math.pow((i - N * 0.6) / (N * 0.18), 2)) * 18
    // Small random noise
    const noise = (Math.random() - 0.5) * 2

    const value = Math.round(trend + wave + bump + noise)

    data.push({
      month: `${month} ${year}`,
      metric: value,
    })
  }

  return data
}

const rawData = generateMockData()

interface MetricCardProps {
  title: string
  subTitle?: string
  content: string | number | undefined
  type: 'money' | 'percentage' | 'number' | 'hour' | 'seconds'
  mtdCurrContent?: number | undefined
  ytdCurrContent?: number | undefined
  mtdPrevContent?: number | undefined
  ytdPrevContent?: number | undefined
  kpiGoal?: number | null
  loading?: boolean
  showToDate?: boolean
  quarterToDate?: boolean
  icon: React.ReactNode
  color: { bg: string; color: string }
  isIncreasingPositive?: boolean
  basicCard?: boolean
  isBeta?: boolean
  // TODO: remove this after finalize
  isNewLogic?: boolean
}

const formatContent = (
  content: string | number | undefined,
  type: MetricCardProps['type']
) => {
  switch (type) {
    case 'hour':
      return (
        <span>
          {formatNumber(content, type, false)}
          <span className="text-sm">hr</span>
        </span>
      )
    case 'seconds':
      return (
        <span>
          {formatNumber(content, type, false)}
          <span className="text-sm">secs</span>
        </span>
      )
    case 'number':
    case 'money':
    case 'percentage':
      return formatNumber(content, type)
    default:
      return content
  }
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  mtdCurrContent,
  ytdCurrContent,
  mtdPrevContent,
  ytdPrevContent,
  subTitle,
  content,
  type,
  loading = false,
  showToDate = true,
  quarterToDate = false,
  kpiGoal,
  icon,
  color,
  isIncreasingPositive = true,
  basicCard = false,
  isBeta = false,
  isNewLogic = false,
}) => {
  return (
    <Card>
      <CardContent className="p-6">
        <div
          className={
            basicCard ? 'flex flex-col space-y-1' : 'flex flex-col space-y-3'
          }
          style={{ position: 'relative' }}
        >
          {/* Header with title and icon */}
          <div className="flex justify-between items-start">
            <div
              className={basicCard ? 'h-12 flex flex-col justify-start' : ''}
            >
              <span className="text-text-l text-gray-500">{title}</span>
              <p className="text-text-m text-gray-500">
                {subTitle || '\u00A0'}
              </p>
            </div>
            {icon && color && (
              <div
                className={`w-8 h-8 min-w-8 rounded-full flex items-center justify-center flex-shrink-0 ${color.bg}`}
              >
                {React.isValidElement(icon)
                  ? React.cloneElement(icon as React.ReactElement<any>)
                  : icon}
              </div>
            )}
          </div>

          {/* Line Chart */}
          {/* {!loading && !basicCard && (
            <div className="absolute right-2 top-[50%] -translate-y-1/2 w-3/5 h-20 pointer-events-none z-10">
              <ReactEcharts
                option={getLineChartOptions(color.color)}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          )} */}

          {/* Main value */}
          <div
            className={`flex justify-between items-center ${basicCard ? 'mt-1' : ''}`}
          >
            {!loading && checkIfValidContent(content) ? (
              <p className="text-display-l text-black-500">
                {formatContent(content, type)}
              </p>
            ) : (
              !loading && <p className="text-4xl font-bold text-gray-900">-</p>
            )}
          </div>

          {loading ? (
            <Loading />
          ) : (
            <>
              {showToDate && !basicCard && (
                <div className="space-y-3">
                  {/* MTD and YTD trends */}
                  <div className="flex items-center space-x-6">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="flex items-center space-x-2">
                            <span className="text-text-m text-gray-500">
                              {quarterToDate ? 'QoQ' : 'MoM'}
                            </span>
                            <TrendLabel
                              oldValue={mtdPrevContent || 0}
                              newValue={mtdCurrContent || 0}
                              isNormal={isIncreasingPositive}
                              trendType={type}
                              isMetricCard={true}
                              isNewLogic={isNewLogic}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span>
                            {quarterToDate
                              ? 'Quarter over Quarter'
                              : 'Month over Month'}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="flex items-center space-x-2">
                            <span className="text-text-m text-gray-500">
                              YoY
                            </span>
                            <TrendLabel
                              oldValue={ytdPrevContent || 0}
                              newValue={ytdCurrContent || 0}
                              isNormal={isIncreasingPositive}
                              trendType={type}
                              isMetricCard={true}
                              isNewLogic={isNewLogic}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span>Year over Year</span>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {kpiGoal && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="flex items-center space-x-2">
                              <span className="text-text-m text-gray-500">
                                KPI
                              </span>
                              <div className="text-text-m text-gray-500">
                                {kpiGoal ? formatContent(kpiGoal, type) : '-'}
                              </div>
                              {/* <TrendLabel
                              oldValue={ytdPrevContent || 0}
                              newValue={ytdCurrContent || 0}
                              isNormal={true}
                              trendType={type}
                              isMetricCard={true}
                            /> */}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>KPI Goal</span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
      {isBeta && (
        <CardFooter className="flex justify-end items-end p-0">
          <BetaLabel />
        </CardFooter>
      )}
    </Card>
  )
}

function checkIfValidContent(content: string | number | undefined) {
  if (content === 0) return true

  return (
    content && content !== undefined && content !== null && content !== 'NaN'
  )
}

const getLineChartOptions = (color: string) => {
  return {
    grid: {
      bottom: '0%',
      left: '0%',
      right: '0%',
      top: '0%',
    },
    xAxis: {
      type: 'category',
      show: false,
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [
      {
        name: 'Metric',
        type: 'line',
        data: rawData.map((item: any) => item.metric),
        itemStyle: {
          color: color,
        },
        lineStyle: {
          color: color,
          width: 2,
        },
        symbol: 'none',
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: color + '40',
              },
              {
                offset: 1,
                color: color + '00',
              },
            ],
          },
        },
      },
    ],
  }
}

export { MetricCard }
